import { useApolloClient } from '@apollo/client';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  edmsSignalCategory,
  machineSeriesExtent,
  windowMachineSeries,
  type MachineSeries,
} from 'tf-pulse-shared';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import {
  GetMachineDataDocument,
  GetMachineDataQuery,
  GetMachineDataQueryVariables,
  MetricTimespan,
  MetricType,
  useGetMachineDataQuery,
  useGetMachineMetricDataQuery,
} from '../../gql/generated';
import { formatUnit, isMachineAlarmSignal } from '../../utils/formatMetric';
import { MachineBarChart } from './MachineBarChart';
import { MachineHistoryGraphLoading } from './MachineHistoryGraphLoading';
import { MachineLaneChart } from './MachineLaneChart';
import { MachineLineChart } from './MachineLineChart';
import { MachineTimeBarChart } from './MachineTimeBarChart';
import {
  computeNumDataPoints,
  edmsLookbackMs,
  resolveTimeBounds,
  toEdmsTimeRange,
} from './timeRangeDomain';
import {
  TimeRangeSelector,
  type CustomDateRange,
  type TimeRange,
} from './TimeRangeSelector';

interface MachineHistoryGraphProps {
  /** Machines to display metrics for (with IDs and display names). Single machine = bar chart with min/max, multiple = one lane or line per machine */
  machines: Array<{ id: string; displayName: string }>;
  /** Type of metric to display */
  metricType?: MetricType;
  /** Alarm thresholds to display as reference lines */
  alarmThresholds?: {
    minimum?: number;
    maximum?: number;
  };
  /** Optional signal category to fetch actual time series data from EDMS */
  signalCategory?: string;
  /** Optional signal name to fetch actual time series data from EDMS */
  signal?: string;
  /** Optional unit of measurement (e.g., '°C', 'bar', 'kg') */
  unit?: string;
  /** Callback when thresholds are calculated from data (min/max values) */
  onThresholdsCalculated?: (thresholds: {
    minimum: number;
    maximum: number;
  }) => void;
  /** Optional timestamp (ms) when the alarm was triggered - shows indicator on graph */
  alarmTriggerTime?: number;
  /** Optional initial time range to display (defaults to '24h') */
  initialTimeRange?: TimeRange;
  /** Left side of the top row, next to the time range selector. Defaults to a "History" label. */
  header?: ReactNode;
}

/**
 * MachineHistoryGraph - Displays machine metric data over time
 *
 * Fetches data from the GraphQL backend based on machines, metric type, and timespan.
 * - Single machine: Shows min/max/average data as bars (captures spikes within each resolution)
 * - Multiple machines: One on/off lane (boolean) or one line (numeric) per machine, nothing merged
 * - With alarmId: Fetches actual alarm time series data from EDMS
 *
 * @example
 * // Single machine temperature with min/max bars
 * <MachineHistoryGraph
 *   machines={[{ id: 'machine-123', displayName: 'Production Line A' }]}
 *   metricType={MetricType.TEMPERATURE}
 * />
 *
 * @example
 * // Multiple machines pressure comparison
 * <MachineHistoryGraph
 *   machines={[
 *     { id: 'machine-123', displayName: 'Production Line A' },
 *     { id: 'machine-456', displayName: 'Production Line B' },
 *     { id: 'machine-789', displayName: 'Production Line C' }
 *   ]}
 *   metricType={MetricType.PRESSURE}
 * />
 *
 * @example
 * // Machine with actual time series data for a specific signal
 * <MachineHistoryGraph
 *   machines={[{ id: 'machine-123', displayName: 'Production Line A' }]}
 *   signalCategory="Temperature"
 *   signal="CoolantTemp"
 *   alarmThresholds={{ minimum: 25, maximum: 75 }}
 * />
 */
export function MachineHistoryGraph({
  machines = [],
  metricType = MetricType.TEMPERATURE,
  alarmThresholds,
  signalCategory,
  signal,
  unit: unitProp,
  onThresholdsCalculated,
  alarmTriggerTime,
  initialTimeRange = '24h',
  header,
}: MachineHistoryGraphProps) {
  const apolloClient = useApolloClient();
  const isSignalMode = Boolean(signal && signalCategory);
  // EDMS's data API names the on/off alarms category "machine-alarms";
  // alerts saved before 2026-09-04 carry the spelling "MachineAlarm".
  const edmsCategory = edmsSignalCategory(signalCategory);
  const isBooleanSignal = isMachineAlarmSignal({
    unit: unitProp,
    signalCategory,
  });
  const [selectedRange, setSelectedRange] =
    useState<TimeRange>(initialTimeRange);
  const [customRange, setCustomRange] = useState<CustomDateRange | undefined>();

  // State for aggregated signal data (fetched for all machines)
  const [allMachinesSignalData, setAllMachinesSignalData] = useState<
    Map<
      string,
      Array<{ time: number; min: number; max: number; average: number }>
    >
  >(new Map());
  const [allMachinesLoading, setAllMachinesLoading] = useState(false);
  const [allMachinesError, setAllMachinesError] = useState<Error | null>(null);

  // Memoized callbacks to prevent TimeRangeSelector re-renders
  const handleRangeChange = useCallback((range: TimeRange) => {
    setSelectedRange(range);
  }, []);

  const handleCustomRangeChange = useCallback((range: CustomDateRange) => {
    setCustomRange(range);
  }, []);

  // Extract machine IDs from machines array
  const machineIds = useMemo(() => machines.map((m) => m.id), [machines]);
  const machineIdsKey = useMemo(() => machineIds.join(','), [machineIds]);

  // Navigation state: 0 to machines.length-1 = individual machines, machines.length = all machines
  // Start with aggregate view (all machines) when there are multiple machines
  const [currentViewIndex, setCurrentViewIndex] = useState(
    machines.length > 1 ? machines.length : 0
  );

  // Determine if we should show aggregate view (all machines)
  const isAggregateView = currentViewIndex === machines.length;

  // Get the machine IDs for the current view
  const currentMachineIds = isAggregateView
    ? machineIds // All machines for aggregate view
    : [machineIds[currentViewIndex]]; // Single machine for individual view

  // Map TimeRange to MetricTimespan enum (for non-signal metric mode only)
  const timespanMap: Record<string, MetricTimespan> = {
    '24h': MetricTimespan.TWENTY_FOUR_HOURS,
    '7d': MetricTimespan.SEVEN_DAYS,
    // GraphQL enum has no 30-day value; closest fallback for non-signal mode.
    '30d': MetricTimespan.SIX_MONTHS,
    '6m': MetricTimespan.SIX_MONTHS,
    '1y': MetricTimespan.ONE_YEAR,
  };

  // One window drives the fetch, the bar positions and the axis labels, so the
  // three can never disagree about what "last 30 days" means.
  const { chartTimeBounds, edmsTimeRange, edmsNumDataPoints } = useMemo(() => {
    const now = Date.now();
    const bounds = resolveTimeBounds(selectedRange, customRange, now);
    // EDMS counts back from now, so a range ending in the past is still fetched
    // from now back to its start.
    const lookbackMs = edmsLookbackMs(bounds, now);

    return {
      chartTimeBounds: bounds,
      edmsTimeRange: toEdmsTimeRange(lookbackMs),
      edmsNumDataPoints: computeNumDataPoints(lookbackMs),
    };
  }, [selectedRange, customRange]);

  // Fetch signal data for ALL machines when in signal mode with multiple machines
  // This allows us to aggregate data for the "All machines" view
  useEffect(() => {
    if (!isSignalMode || machines.length <= 1) {
      setAllMachinesSignalData(new Map());
      return;
    }

    const fetchAllMachinesData = async () => {
      setAllMachinesLoading(true);
      setAllMachinesError(null);

      try {
        const machineIdsForEffect = machineIdsKey
          ? machineIdsKey.split(',')
          : [];
        const promises = machineIdsForEffect.map((machineId) =>
          apolloClient.query<GetMachineDataQuery, GetMachineDataQueryVariables>(
            {
              query: GetMachineDataDocument,
              variables: {
                id: machineId,
                signalCategory: edmsCategory,
                signal: signal,
                timeRange: edmsTimeRange,
                numDataPoints: edmsNumDataPoints,
              },
              fetchPolicy: 'network-only',
            }
          )
        );

        const results = await Promise.all(promises);

        const dataMap = new Map<
          string,
          Array<{ time: number; min: number; max: number; average: number }>
        >();

        results.forEach((result, index) => {
          const machineId = machineIdsForEffect[index];
          if (result.data?.machineData?.items?.length) {
            dataMap.set(
              machineId,
              result.data.machineData.items.map((item) => ({
                time: new Date(item.timeUtc).getTime(),
                min: item.min,
                max: item.max,
                average: item.avg,
              }))
            );
          }
        });

        setAllMachinesSignalData(dataMap);
      } catch (err) {
        setAllMachinesError(
          err instanceof Error ? err : new Error('Failed to fetch machine data')
        );
      } finally {
        setAllMachinesLoading(false);
      }
    };

    fetchAllMachinesData();
  }, [
    isSignalMode,
    machines.length,
    machineIdsKey,
    edmsCategory,
    signal,
    edmsTimeRange,
    edmsNumDataPoints,
    apolloClient,
  ]);

  // Query machine data if signal is provided (for actual time series data)
  // Used for single machine view in signal mode
  const {
    data: machineData,
    loading: machineLoading,
    error: machineError,
  } = useGetMachineDataQuery({
    variables: {
      id: currentMachineIds[0], // Use first machine for signal data
      signalCategory: edmsCategory,
      signal: signal,
      timeRange: edmsTimeRange,
      numDataPoints: edmsNumDataPoints,
    },
    skip: !isSignalMode || machines.length === 0 || isAggregateView, // Skip if aggregate view (use allMachinesSignalData instead)
    fetchPolicy: 'network-only', // Always fetch fresh data
  });

  // Query machine metric data (fallback when no signal is provided)
  const {
    data: metricData,
    loading: metricLoading,
    error: metricError,
  } = useGetMachineMetricDataQuery({
    variables: {
      input: {
        machineIds: currentMachineIds,
        metricType,
        timespan:
          timespanMap[selectedRange] ?? MetricTimespan.TWENTY_FOUR_HOURS,
      },
    },
    skip: machines.length === 0 || isSignalMode, // Do not use mock metric data when real signal is requested
    fetchPolicy: 'network-only', // Always fetch fresh data
  });

  // Transform machine data to match the expected format
  // For single machine view in signal mode
  const transformedMachineData = useMemo(() => {
    if (!isSignalMode || !machineData?.machineData?.items?.length) {
      return undefined;
    }

    return {
      getMachineMetricData: {
        dataPoints: machineData.machineData.items.map((item) => ({
          time: new Date(item.timeUtc).getTime(), // Convert ISO string to timestamp
          min: item.min,
          max: item.max,
          average: item.avg,
          label: undefined,
        })),
        rangeData: null,
        unit: unitProp || '', // Use the unit prop passed from parent
      },
    };
  }, [isSignalMode, machineData, unitProp]);

  // One series per machine for the "All machines" view. Nothing is merged
  // across machines, so an alarm that is on for one machine stays visible
  // next to machines where it is off.
  const comparisonSeries = useMemo<MachineSeries[] | undefined>(() => {
    if (!isSignalMode || !isAggregateView) {
      return undefined;
    }

    return windowMachineSeries(
      machines.map((machine) => ({
        id: machine.id,
        label: machine.displayName,
        points: allMachinesSignalData.get(machine.id) ?? [],
      })),
      chartTimeBounds.from,
      chartTimeBounds.to
    );
  }, [
    isSignalMode,
    isAggregateView,
    machines,
    allMachinesSignalData,
    chartTimeBounds,
  ]);

  // For individual machine view in signal mode with multiple machines,
  // use data from the allMachinesSignalData map instead of making a new query
  const individualMachineFromCache = useMemo(() => {
    if (
      !isSignalMode ||
      isAggregateView ||
      machines.length <= 1 ||
      allMachinesSignalData.size === 0
    ) {
      return undefined;
    }

    const machineId = machineIds[currentViewIndex];
    const cachedData = allMachinesSignalData.get(machineId);

    if (!cachedData || cachedData.length === 0) {
      return undefined;
    }

    return {
      getMachineMetricData: {
        dataPoints: cachedData.map((item) => ({
          ...item,
          label: undefined,
        })),
        rangeData: null,
        unit: unitProp || '',
      },
    };
  }, [
    isSignalMode,
    isAggregateView,
    machines.length,
    allMachinesSignalData,
    machineIds,
    currentViewIndex,
    unitProp,
  ]);

  // Use real signal data when available; otherwise, use metric data (non-signal mode only)
  // Priority: individualMachineFromCache > transformedMachineData > metricData
  const rawData = isSignalMode
    ? isAggregateView
      ? undefined // the per-machine view draws comparisonSeries instead
      : individualMachineFromCache || transformedMachineData
    : metricData;

  // EDMS always counts back from now, so a range ending in the past comes back
  // with points past its end. Drop everything outside the window before it
  // reaches the chart, the Y-axis scale or the derived thresholds.
  // Metric mode is index-based mock data with no real timestamps, so leave it be.
  const data = useMemo(() => {
    if (!isSignalMode || !rawData) {
      return rawData;
    }

    const inWindow = <T extends { time: number }>(points: T[]) =>
      points.filter(
        (point) =>
          point.time >= chartTimeBounds.from && point.time <= chartTimeBounds.to
      );

    const { dataPoints, rangeData, unit } = rawData.getMachineMetricData;

    return {
      getMachineMetricData: {
        dataPoints: dataPoints ? inWindow(dataPoints) : dataPoints,
        rangeData: rangeData ? inWindow(rangeData) : rangeData,
        unit,
      },
    };
  }, [isSignalMode, rawData, chartTimeBounds]);

  // Determine loading state
  const loading = isSignalMode
    ? machines.length > 1
      ? allMachinesLoading
      : machineLoading
    : metricLoading;

  // Determine error state
  const error = isSignalMode
    ? machines.length > 1
      ? allMachinesError
      : machineError
    : metricError;

  const unit = formatUnit(
    comparisonSeries ? unitProp : data?.getMachineMetricData.unit
  );

  // Handle no data - calculate before useMemo
  // When using signal data, we always use dataPoints (rangeData is null)
  // In signal mode, always check dataPoints since rangeData doesn't exist
  const hasData = comparisonSeries
    ? comparisonSeries.some((machine) => machine.points.length > 0)
    : isSignalMode
      ? Boolean(data?.getMachineMetricData.dataPoints?.length)
      : isAggregateView
        ? Boolean(data?.getMachineMetricData.rangeData?.length)
        : Boolean(data?.getMachineMetricData.dataPoints?.length);

  // Derive thresholds from data using useMemo (avoids cascading renders)
  const calculatedThresholds = useMemo(() => {
    if (!hasData || loading) {
      return undefined;
    }

    if (comparisonSeries) {
      const extent = machineSeriesExtent(comparisonSeries);
      return extent ? { minimum: extent.min, maximum: extent.max } : undefined;
    }

    if (!data) {
      return undefined;
    }

    // When using signal data, always use dataPoints
    const dataPoints =
      signal && signalCategory
        ? data.getMachineMetricData.dataPoints
        : isAggregateView
          ? data.getMachineMetricData.rangeData
          : data.getMachineMetricData.dataPoints;

    if (!dataPoints || dataPoints.length === 0) {
      return undefined;
    }

    const allMins = dataPoints.map((d) => d.min);
    const allMaxs = dataPoints.map((d) => d.max);
    const overallMin = Math.min(...allMins);
    const overallMax = Math.max(...allMaxs);

    return {
      minimum: overallMin,
      maximum: overallMax,
    };
  }, [
    data,
    comparisonSeries,
    hasData,
    loading,
    isAggregateView,
    signal,
    signalCategory,
  ]);

  // Notify parent component of calculated thresholds
  useEffect(() => {
    if (calculatedThresholds) {
      onThresholdsCalculated?.(calculatedThresholds);
    }
  }, [calculatedThresholds, onThresholdsCalculated]);

  // Use alarmThresholds if provided, otherwise use calculated thresholds
  const effectiveThresholds = alarmThresholds || calculatedThresholds;

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentViewIndex((prev) => {
      if (prev === 0) {
        // Wrap to end (all machines view)
        return machines.length;
      }
      return prev - 1;
    });
  };

  const handleNext = () => {
    setCurrentViewIndex((prev) => {
      if (prev === machines.length) {
        // Wrap to start (first machine)
        return 0;
      }
      return prev + 1;
    });
  };

  // Get current view label
  const getCurrentViewLabel = () => {
    if (isAggregateView) {
      return `All ${machines.length} machines`;
    }

    // Use the machine display name
    const machine = machines[currentViewIndex];
    return (
      machine?.displayName ||
      `Machine ${currentViewIndex + 1} of ${machines.length}`
    );
  };

  // Calculate dynamic X-axis labels based on timespan
  const getXAxisLabels = () => {
    // For signal mode, label the same window the bars are drawn in
    if (isSignalMode) {
      const startTime = chartTimeBounds.from;
      const endTime = chartTimeBounds.to;
      const rangeMs = endTime - startTime;

      // Pick label format based on duration, not the preset key
      const formatLabel = (time: Date): string => {
        const hours = rangeMs / (60 * 60 * 1000);
        if (hours <= 48) {
          // ≤ 2 days → show time (HH:mm)
          return time.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
        }
        if (hours <= 14 * 24) {
          // ≤ 14 days → show day + date (Mon 5)
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          return `${days[time.getDay()]} ${time.getDate()}`;
        }
        // Longer → month name
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        return monthNames[time.getMonth()];
      };

      // Generate 4 labels evenly spaced
      return Array.from({ length: 4 }, (_, i) => {
        const time = new Date(startTime + (rangeMs * i) / 3);
        return formatLabel(time);
      });
    }

    // Metric mode (mock data) has no real timestamps; label from the first point
    const timeData =
      data?.getMachineMetricData.dataPoints ||
      data?.getMachineMetricData.rangeData;

    if (!timeData || timeData.length === 0) {
      return ['00:00', '06:00', '12:00', '18:00'];
    }

    const firstTime = new Date(timeData[0].time);

    switch (selectedRange) {
      case '24h': {
        // Show 4 time points across 24 hours
        return ['00:00', '06:00', '12:00', '18:00'];
      }
      case '7d': {
        // Show day names
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return Array.from({ length: 4 }, (_, i) => {
          const date = new Date(firstTime);
          date.setDate(date.getDate() + (i * 7) / 3);
          return days[date.getDay()];
        });
      }
      case '6m': {
        // Show month names
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        return Array.from({ length: 4 }, (_, i) => {
          const date = new Date(firstTime);
          date.setMonth(date.getMonth() + (i * 6) / 3);
          return monthNames[date.getMonth()];
        });
      }
      case '30d': {
        // Show day + month labels across roughly one month.
        return Array.from({ length: 4 }, (_, i) => {
          const date = new Date(firstTime);
          date.setDate(date.getDate() + (i * 30) / 3);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
          });
        });
      }
      case '1y': {
        // Show quarter or month labels
        return Array.from({ length: 4 }, (_, i) => {
          const date = new Date(firstTime);
          date.setMonth(date.getMonth() + (i * 12) / 3);
          return `Q${Math.floor(date.getMonth() / 3) + 1}`;
        });
      }
      default:
        return ['00:00', '06:00', '12:00', '18:00'];
    }
  };

  const xAxisLabels = getXAxisLabels();

  return (
    <div className='flex flex-col gap-2'>
      {/* Header with Time Range Selector - Always rendered to prevent remounting */}
      <div className='flex items-center justify-between gap-2'>
        {header ?? <div className='size-p2 text-primary'>History</div>}

        <TimeRangeSelector
          selectedRange={selectedRange}
          customRange={customRange}
          onRangeChange={handleRangeChange}
          onCustomRangeChange={handleCustomRangeChange}
        />
      </div>

      {/* Conditional Content based on state */}
      {loading ? (
        <MachineHistoryGraphLoading />
      ) : error ? (
        <>
          <div className='flex h-52.5 items-center justify-center'>
            <div className='text-error'>Error loading metric data</div>
          </div>

          {/* Navigation - Allow browsing even with errors */}
          {machines.length > 1 && (
            <div
              className='flex min-w-0 items-center justify-between gap-2 px-20
                pt-3 pb-2'
            >
              <button
                className='text-primary flex h-8 w-8 shrink-0 cursor-pointer
                  items-center justify-center transition-colors
                  hover:opacity-70'
                aria-label='Previous'
                onClick={handlePrevious}
              >
                <ChevronLeftIcon className='h-6 w-6' />
              </button>

              <div
                className='size-p2 text-primary min-w-0 flex-1 truncate
                  text-center font-bold'
                title={getCurrentViewLabel()}
              >
                {getCurrentViewLabel()}
              </div>

              <button
                className='text-primary flex h-8 w-8 shrink-0 cursor-pointer
                  items-center justify-center justify-self-end transition-colors
                  hover:opacity-70'
                aria-label='Next'
                onClick={handleNext}
              >
                <ChevronRightIcon className='h-6 w-6' />
              </button>
            </div>
          )}
        </>
      ) : !hasData ? (
        <>
          <div className='flex h-52.5 items-center justify-center'>
            <div className='text-secondary'>No metric data available</div>
          </div>

          {/* Navigation - Allow browsing even with no data */}
          {machines.length > 1 && (
            <div
              className='flex min-w-0 items-center justify-between gap-2 px-20
                pt-3 pb-2'
            >
              <button
                className='text-primary flex h-8 w-8 shrink-0 cursor-pointer
                  items-center justify-center transition-colors
                  hover:opacity-70'
                aria-label='Previous'
                onClick={handlePrevious}
              >
                <ChevronLeftIcon className='h-6 w-6' />
              </button>

              <div
                className='size-p2 text-primary min-w-0 flex-1 truncate
                  text-center font-bold'
                title={getCurrentViewLabel()}
              >
                {getCurrentViewLabel()}
              </div>

              <button
                className='text-primary flex h-8 w-8 shrink-0 cursor-pointer
                  items-center justify-center justify-self-end transition-colors
                  hover:opacity-70'
                aria-label='Next'
                onClick={handleNext}
              >
                <ChevronRightIcon className='h-6 w-6' />
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Graph, fixed height so the machine navigation never moves */}
          <div className='flex h-52.5 flex-col gap-2.5'>
            {/* Render chart with appropriate data */}
            {/* All machines: one lane or line per machine. One machine: TimeBarChart (real timestamps). Metric mode: BarChart (index-based) */}
            {comparisonSeries ? (
              isBooleanSignal ? (
                <MachineLaneChart
                  series={comparisonSeries}
                  timeBounds={chartTimeBounds}
                  xAxisLabels={xAxisLabels}
                  alarmTriggerTime={alarmTriggerTime}
                />
              ) : (
                <MachineLineChart
                  series={comparisonSeries}
                  unit={unit}
                  timeBounds={chartTimeBounds}
                  xAxisLabels={xAxisLabels}
                  alarmThresholds={effectiveThresholds}
                  alarmTriggerTime={alarmTriggerTime}
                />
              )
            ) : isSignalMode ? (
              <MachineTimeBarChart
                dataPoints={data!.getMachineMetricData.dataPoints!}
                unit={unit}
                alarmThresholds={effectiveThresholds}
                alarmTriggerTime={alarmTriggerTime}
                timeBounds={chartTimeBounds}
                isBooleanSignal={isBooleanSignal}
              />
            ) : (
              <MachineBarChart
                dataPoints={
                  isAggregateView
                    ? data!.getMachineMetricData.rangeData!
                    : data!.getMachineMetricData.dataPoints!
                }
                unit={unit}
                alarmThresholds={effectiveThresholds}
                isBooleanSignal={isBooleanSignal}
              />
            )}

            {/* X-axis Labels (the per-machine charts draw their own) */}
            {!comparisonSeries && (
              <div
                className='size-caption text-secondary flex items-center
                  justify-between pl-10.75'
              >
                {xAxisLabels.map((label, index) => (
                  <span key={index}>{label}</span>
                ))}
              </div>
            )}
          </div>

          {/* Navigation */}
          {machines.length > 1 && (
            <div
              className='flex min-w-0 items-center justify-between gap-2 px-20
                pt-3 pb-2'
            >
              <button
                className='text-primary flex h-8 w-8 shrink-0 cursor-pointer
                  items-center justify-center transition-colors
                  hover:opacity-70'
                aria-label='Previous'
                onClick={handlePrevious}
              >
                <ChevronLeftIcon className='h-6 w-6' />
              </button>

              <div
                className='size-p2 text-primary min-w-0 flex-1 truncate
                  text-center font-bold'
                title={getCurrentViewLabel()}
              >
                {getCurrentViewLabel()}
              </div>

              <button
                className='text-primary flex h-8 w-8 shrink-0 cursor-pointer
                  items-center justify-center justify-self-end transition-colors
                  hover:opacity-70'
                aria-label='Next'
                onClick={handleNext}
              >
                <ChevronRightIcon className='h-6 w-6' />
              </button>
            </div>
          )}

          {/* Single Machine Label */}
          {machines.length === 1 && (
            <div
              className='flex min-w-0 items-center justify-center px-20 pt-3
                pb-2'
            >
              <div
                className='size-p2 text-primary h-4.75 min-w-0 truncate
                  font-bold'
                title={machines[0].displayName}
              >
                {machines[0].displayName}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
